/* eslint-disable react-hooks/exhaustive-deps */
import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import {
  type PartialKeys,
  useVirtualizer,
  type VirtualizerOptions,
} from '@tanstack/react-virtual'
import { type RefObject, useEffect, useRef } from 'react'

interface UseVirtualRowsProps<
  T = unknown,
  ParentElement extends HTMLElement = HTMLElement,
> extends Partial<
    PartialKeys<
      VirtualizerOptions<ParentElement, ParentElement>,
      'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
    >
  > {
  infiniteQuery: {
    hasNextPage: UseInfiniteQueryResult<T, Error>['hasNextPage']
    isFetchingNextPage: UseInfiniteQueryResult<T, Error>['isFetchingNextPage']
    fetchNextPage: UseInfiniteQueryResult<T, Error>['fetchNextPage']
  }
  rows: unknown[]
  /** Manged ref for the parent element. If not provided, the hook will manage & return its own ref. */
  parentRef?: RefObject<ParentElement | null>
}

// inspired by https://tanstack.com/virtual/latest/docs/framework/react/examples/infinite-scroll
export const useVirtualRows = <
  T,
  ParentElement extends HTMLElement = HTMLDivElement,
>(
  props: UseVirtualRowsProps<T, ParentElement>,
) => {
  const {
    infiniteQuery: { hasNextPage, isFetchingNextPage, fetchNextPage },
    rows,
  } = props
  const parentRef = useRef<ParentElement>(null)

  const lastItemRef = useRef(-1)

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => (props.parentRef ?? parentRef).current,
    estimateSize: () => 80,
    ...props,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omitting dependencies to avoid infinite re-renders
  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse()

    if (!lastItem || lastItem.index <= lastItemRef.current) {
      return
    }

    if (
      lastItem.index >= rows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      lastItemRef.current = lastItem.index
      fetchNextPage()
    }
  }, [
    hasNextPage,
    fetchNextPage,
    rows.length,
    isFetchingNextPage,
    rowVirtualizer.getVirtualItems(),
  ])

  return {
    parentRef: props.parentRef ?? parentRef,
    rowVirtualizer,
  }
}
